import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';
import { generators as defaultGeneratorsBlueprint } from '../states/generators';
import { CharacterClass, type GameGenerators, type Generator, type GeneratorType, type ResourceCost } from '../../types/game-types';
import { costToBuyNItems } from '../helpers/calculate-cost';
import { getModifiedGeneratorCost } from '../helpers/get-modified-generator-cost';
import { trySpendResources } from '../helpers/try-spend-resources';
import { useGameStatusStore } from './useGameStatusStore';
import { useCurrencyStore } from './useCurrencyStore';
import { CONFIG } from '../../config';
import { useUpgradesStore } from './useUpgradesStore';

// ---------------------------------------------------------------------------
// Persist merge: keep every `GeneratorType` key valid even if JSON is partial.
// ---------------------------------------------------------------------------

/** Must match `GameGenerators` keys (single source for iteration + merge). */
const GENERATOR_TYPE_KEYS = [
  'warrior',
  'wizard',
  'elf',
  'garrison',
  'academy',
  'outpost',
  'council',
  'nexus',
  'forest',
  'thief',
  'bard',
  'cleric',
  'guild',
  'troupe',
  'seminary',
  'theater',
  'congress',
  'college',
  'catapult',
  'farmer',
  'lumberjack',
  'miner',
] as const satisfies readonly GeneratorType[];

function cloneDefaultGenerators(): GameGenerators {
  return structuredClone(defaultGeneratorsBlueprint) as GameGenerators;
}

function mergeCostRecord(persisted: unknown, template: ResourceCost): ResourceCost {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return { ...template };
  }
  const record = persisted as Record<string, unknown>;
  const next = { ...template } as Record<string, number | undefined>;
  for (const key of Object.keys(template) as (keyof ResourceCost)[]) {
    const value = record[key as string];
    if (typeof value === 'number' && Number.isFinite(value)) {
      next[key as string] = value;
    }
  }
  return next as ResourceCost;
}

/**
 * Merge one persisted generator entry with the current template so old saves
 * pick up new fields (e.g. new currencies on `cost`) without crashing.
 */
function mergeGeneratorRecord(persisted: unknown, template: Generator): Generator {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return { ...template };
  }
  const record = persisted as Record<string, unknown>;
  const next: Generator = { ...template };

  if (typeof record.owned === 'number' && Number.isFinite(record.owned)) next.owned = record.owned;
  if (typeof record.level === 'number' && Number.isFinite(record.level)) next.level = record.level;
  if (typeof record.purchased === 'number' && Number.isFinite(record.purchased)) next.purchased = record.purchased;
  if (typeof record.costGrowthRate === 'number' && Number.isFinite(record.costGrowthRate)) {
    next.costGrowthRate = record.costGrowthRate;
  }
  if (typeof record.baseGeneration === 'number' && Number.isFinite(record.baseGeneration)) {
    next.baseGeneration = record.baseGeneration;
  }
  if (typeof record.isUnlocked === 'boolean') next.isUnlocked = record.isUnlocked;
  if (record.ticksToGenerate === undefined) {
    // keep template default
  } else if (typeof record.ticksToGenerate === 'number' && Number.isFinite(record.ticksToGenerate)) {
    next.ticksToGenerate = record.ticksToGenerate;
  }
  if (typeof record.purchaseLimit === 'number' && Number.isFinite(record.purchaseLimit)) {
    next.purchaseLimit = record.purchaseLimit;
  }
  if (record.cost !== undefined) {
    next.cost = mergeCostRecord(record.cost, template.cost);
  }
  // `id` and `type` should stay on template (enum / literal stability from code).

  return next;
}

function mergePersistedGeneratorsSlice(persisted: unknown, current: GameGenerators): GameGenerators {
  if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
    return current;
  }
  const record = persisted as Record<string, unknown>;
  const rawGenerators = record.generators;
  if (!rawGenerators || typeof rawGenerators !== 'object' || Array.isArray(rawGenerators)) {
    return current;
  }
  const incomingMap = rawGenerators as Record<string, unknown>;
  const next = { ...current };
  for (const type of GENERATOR_TYPE_KEYS) {
    const template = defaultGeneratorsBlueprint[type];
    const raw = incomingMap[type];
    next[type] = mergeGeneratorRecord(raw, template);
  }
  return next;
}

function mergePersistedGeneratorsRoot(persisted: unknown, current: GeneratorsStore): GeneratorsStore {
  if (!persisted || typeof persisted !== 'object') {
    return current;
  }
  return {
    ...current,
    generators: mergePersistedGeneratorsSlice(persisted, current.generators),
  };
}

// ---------------------------------------------------------------------------
// Store types
// ---------------------------------------------------------------------------

export type GeneratorsState = {
  generators: GameGenerators;
};

export type GeneratorsActions = {
  setGenerators: (next: GameGenerators) => void;
  sync: (patch: Partial<GeneratorsState>) => void;
  /** Buy flow: spends via `trySpendResources`; reads `upgrades` from `useUpgradesStore`. */
  buyGenerator: (payload: { id: GeneratorType; amountToBuy: number }) => boolean;
  /** Invasion / rewards style bulk owned changes (warrior, wizard, elf, catapult). */
  addOrRemoveGenerators: (payload: {
    warriors?: number;
    wizards?: number;
    elves?: number;
    catapult?: number;
  }) => void;
  /** Tutorial tier unlock from gold thresholds; reads upgrade costs from `useUpgradesStore`. */
  runUnlockTier: () => boolean;
  /** Cheat / dev: mark every generator unlocked. */
  unlockAllGenerators: () => void;
  reset: () => void;
};

export type GeneratorsStore = GeneratorsState & GeneratorsActions;

const INITIAL_GENERATORS_STATE: GeneratorsState = {
  generators: cloneDefaultGenerators(),
};

export const useGeneratorsStore: PersistedGameStore<GeneratorsStore> = createGameStore<GeneratorsStore>(
  {
    saveKey: 'generators',
    persistConfig: {
      merge: (persisted, current) => mergePersistedGeneratorsRoot(persisted, current as GeneratorsStore),
    },
  },
  (set, get) => ({
    ...INITIAL_GENERATORS_STATE,

    setGenerators: (next) => set({ generators: next }),

    sync: (patch) => set(patch as GeneratorsStore),

    buyGenerator: (payload) => {
      if (useGameStatusStore.getState().stunEffectTickDurationRemaining > 0) {
        return false;
      }

      const upgrades = useUpgradesStore.getState().upgrades;
      const { id: generatorId, amountToBuy } = payload;
      const currentGenerator = get().generators[generatorId];
      const currencies = Object.keys(currentGenerator.cost) as (keyof ResourceCost)[];

      const generatorCost = getModifiedGeneratorCost(upgrades[generatorId], currentGenerator);

      const purchaseCost = currencies.reduce((costObj, currency) => {
        costObj[currency] = costToBuyNItems(
          generatorCost[currency]!,
          currentGenerator.costGrowthRate,
          currentGenerator.purchased,
          amountToBuy,
        );
        return costObj;
      }, {} as ResourceCost);

      if (!trySpendResources(purchaseCost)) {
        return false;
      }

      const mercenariesToBuy = amountToBuy * (upgrades[payload.id]?.teamWork?.multiplier ?? 0);
      const nextGenerator = {
        ...currentGenerator,
        owned: currentGenerator.owned + amountToBuy + mercenariesToBuy,
        purchased: currentGenerator.purchased + amountToBuy,
      };

      set({
        generators: {
          ...get().generators,
          [generatorId]: nextGenerator,
        },
      });
      return true;
    },

    addOrRemoveGenerators: (payload) => {
      const {
        warriors: warriorsToAdd = 0,
        wizards: wizardsToAdd = 0,
        elves: elvesToAdd = 0,
        catapult: catapultsToAdd = 0,
      } = payload;
      const generators = get().generators;
      set({
        generators: {
          ...generators,
          warrior: {
            ...generators.warrior,
            owned: Math.max(generators.warrior.owned + warriorsToAdd, generators.warrior.purchased ?? 0),
          },
          wizard: {
            ...generators.wizard,
            owned: Math.max(generators.wizard.owned + wizardsToAdd, generators.wizard.purchased ?? 0),
          },
          elf: {
            ...generators.elf,
            owned: Math.max(generators.elf.owned + elvesToAdd, generators.elf.purchased ?? 0),
          },
          catapult: {
            ...generators.catapult,
            owned: Math.max(generators.catapult.owned + catapultsToAdd, generators.catapult.purchased ?? 0),
          },
        },
      });
    },

    runUnlockTier: () => {
      const upgrades = useUpgradesStore.getState().upgrades;
      const playerGold = useCurrencyStore.getState().gold;
      const prev = get().generators;
      const next = { ...prev };
      let unlockedAny = false;

      for (const type of GENERATOR_TYPE_KEYS) {
        const generator = next[type];
        if (generator.isUnlocked) continue;

        const unlockThreshold =
          generator.id === CharacterClass.Warrior
            ? 0.1
            : getModifiedGeneratorCost(upgrades[generator.id], generator).gold! * CONFIG.items.unlockAtFraction;

        if (unlockThreshold <= playerGold) {
          next[type] = { ...generator, isUnlocked: true };
          unlockedAny = true;
        }
      }

      if (!unlockedAny) {
        return false;
      }
      set({ generators: next });
      return true;
    },

    unlockAllGenerators: () => {
      const next = { ...get().generators };
      for (const type of GENERATOR_TYPE_KEYS) {
        next[type] = { ...next[type], isUnlocked: true };
      }
      set({ generators: next });
    },

    reset: () => set({ generators: cloneDefaultGenerators() }),
  }),
);

registerStoreReset(() => useGeneratorsStore.getState().reset());

// ---------------------------------------------------------------------------
// Legacy main-blob migration (generators used to live on `GUIGameState` / save blob).
// ---------------------------------------------------------------------------

function generatorsMatchFactoryDefaults(current: GameGenerators): boolean {
  try {
    return JSON.stringify(current) === JSON.stringify(cloneDefaultGenerators());
  } catch {
    return false;
  }
}

/**
 * When the dedicated `generators` persist slice is still factory defaults but the monolithic
 * save blob still carries a `generators` object (pre–Zustand-split saves), merge that blob once.
 * Skips when Zustand already hydrated real progress so we do not clobber with a stale blob.
 */
export function applyLegacyGeneratorsFromMainSaveIfStoreStillDefault(legacyGenerators: unknown): void {
  if (legacyGenerators === undefined || legacyGenerators === null) {
    return;
  }
  if (!generatorsMatchFactoryDefaults(useGeneratorsStore.getState().generators)) {
    return;
  }
  const merged = mergePersistedGeneratorsSlice({ generators: legacyGenerators }, cloneDefaultGenerators());
  useGeneratorsStore.getState().setGenerators(merged);
}
